/**
 * Chart component — abstraction over TradingView Lightweight Charts.
 *
 * Designed to be swappable for TradingView Advanced Charts later without
 * changing consumers. Exposes a declarative overlay API:
 *   <Chart
 *     bars={[{t, o, h, l, c, v}, ...]}
 *     timeframe="1D" onTimeframeChange={fn}
 *     overlays={{
 *       priceLines: [{ price, color, style: 'solid|dashed|dotted', title, testId }],
 *       markers: [{ time, position: 'aboveBar|belowBar', color, shape: 'arrowUp|arrowDown|circle', text }],
 *       movingAverages: [{ period: 20, color: '#22c55e', enabled: true }],
 *     }}
 *   />
 */
import { useEffect, useRef } from "react";
import { createChart, CrosshairMode, LineStyle, CandlestickSeries, HistogramSeries, LineSeries, createSeriesMarkers } from "lightweight-charts";

const TF_LABELS = { "5m": "5m", "15m": "15m", "1h": "1h", "4h": "4h", "1D": "1D", "1W": "1W" };
const STYLE_MAP = {
  solid: LineStyle.Solid,
  dashed: LineStyle.Dashed,
  dotted: LineStyle.Dotted,
  large_dashed: LineStyle.LargeDashed,
};

function sma(bars, period) {
  const out = [];
  for (let i = 0; i < bars.length; i++) {
    if (i < period - 1) continue;
    let s = 0;
    for (let k = 0; k < period; k++) s += bars[i - k].c;
    out.push({ time: Math.floor(new Date(bars[i].t).getTime() / 1000), value: s / period });
  }
  return out;
}

export default function Chart({
  bars = [],
  timeframe = "1D",
  onTimeframeChange,
  timeframes = ["5m", "15m", "1h", "4h", "1D", "1W"],
  overlays = {},
  height = 420,
  showVolume = true,
  loading = false,
}) {
  const rootRef = useRef(null);
  const chartRef = useRef(null);
  const candleRef = useRef(null);
  const volRef = useRef(null);
  const priceLinesRef = useRef([]);
  const maSeriesRef = useRef([]);
  const markersRef = useRef(null);

  // Mount chart once
  useEffect(() => {
    if (!rootRef.current) return;
    const chart = createChart(rootRef.current, {
      localization: { locale: "en-US" },
      layout: {
        background: { type: "solid", color: "#050505" },
        textColor: "#a1a1aa",
        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
        fontSize: 11,
      },
      grid: {
        vertLines: { color: "rgba(255,255,255,0.03)" },
        horzLines: { color: "rgba(255,255,255,0.05)" },
      },
      rightPriceScale: { borderColor: "rgba(255,255,255,0.08)" },
      timeScale: { borderColor: "rgba(255,255,255,0.08)", timeVisible: true, secondsVisible: false },
      crosshair: { mode: CrosshairMode.Normal, vertLine: { color: "rgba(255,255,255,0.25)" }, horzLine: { color: "rgba(255,255,255,0.25)" } },
      handleScroll: true, handleScale: true,
    });
    chartRef.current = chart;
    const candles = chart.addSeries(CandlestickSeries, {
      upColor: "#34C759", downColor: "#FF3B30",
      wickUpColor: "#34C759", wickDownColor: "#FF3B30",
      borderUpColor: "#34C759", borderDownColor: "#FF3B30",
      priceFormat: { type: "price", precision: 2, minMove: 0.01 },
    });
    candleRef.current = candles;

    if (showVolume) {
      const vol = chart.addSeries(HistogramSeries, {
        color: "rgba(59,130,246,0.35)",
        priceFormat: { type: "volume" },
        priceScaleId: "volume",
      });
      chart.priceScale("volume").applyOptions({
        scaleMargins: { top: 0.82, bottom: 0 },
      });
      volRef.current = vol;
    }

    const onResize = () => chart.applyOptions({ width: rootRef.current.clientWidth });
    onResize();
    window.addEventListener("resize", onResize);
    return () => { window.removeEventListener("resize", onResize); chart.remove(); };
  }, [showVolume]);

  // Data (bars)
  useEffect(() => {
    if (!candleRef.current || !bars?.length) return;
    const candleData = bars.map((b) => ({
      time: Math.floor(new Date(b.t).getTime() / 1000),
      open: b.o, high: b.h, low: b.l, close: b.c,
    }));
    candleRef.current.setData(candleData);

    if (volRef.current) {
      const volData = bars.map((b) => ({
        time: Math.floor(new Date(b.t).getTime() / 1000),
        value: b.v,
        color: b.c >= b.o ? "rgba(52,199,89,0.35)" : "rgba(255,59,48,0.35)",
      }));
      volRef.current.setData(volData);
    }

    // Moving averages
    maSeriesRef.current.forEach((s) => chartRef.current.removeSeries(s));
    maSeriesRef.current = [];
    (overlays.movingAverages || []).forEach((ma) => {
      if (!ma.enabled) return;
      const line = chartRef.current.addSeries(LineSeries, {
        color: ma.color || "#FBBF24", lineWidth: 1, priceLineVisible: false, lastValueVisible: false,
      });
      line.setData(sma(bars, ma.period));
      maSeriesRef.current.push(line);
    });

    chartRef.current.timeScale().fitContent();
  }, [bars, overlays.movingAverages]);

  // Price lines
  useEffect(() => {
    if (!candleRef.current) return;
    priceLinesRef.current.forEach((pl) => candleRef.current.removePriceLine(pl));
    priceLinesRef.current = [];
    (overlays.priceLines || []).forEach((pl) => {
      if (pl.price == null) return;
      const line = candleRef.current.createPriceLine({
        price: pl.price,
        color: pl.color || "#a1a1aa",
        lineStyle: STYLE_MAP[pl.style || "dashed"] ?? LineStyle.Dashed,
        lineWidth: pl.lineWidth || 1,
        axisLabelVisible: true,
        title: pl.title || "",
      });
      priceLinesRef.current.push(line);
    });
  }, [overlays.priceLines]);

  // Markers
  useEffect(() => {
    if (!candleRef.current) return;
    const marks = (overlays.markers || []).map((m) => ({
      time: Math.floor(new Date(m.time).getTime() / 1000),
      position: m.position || "belowBar",
      color: m.color || "#0A84FF",
      shape: m.shape || "circle",
      text: m.text || "",
    }));
    if (!markersRef.current) {
      markersRef.current = createSeriesMarkers(candleRef.current, marks);
    } else {
      markersRef.current.setMarkers(marks);
    }
  }, [overlays.markers]);

  return (
    <div className="bg-card border border-border/60">
      <div className="flex items-center gap-1 border-b border-border/60 px-2 h-9">
        <div className="label-cap px-2">Chart</div>
        <div className="ml-auto flex items-center gap-px">
          {timeframes.map((tf) => (
            <button
              key={tf}
              type="button"
              onClick={() => onTimeframeChange?.(tf)}
              data-testid={`chart-tf-${tf}`}
              className={`px-2.5 h-6 text-[11px] mono uppercase tracking-widest transition-colors duration-150 ${
                timeframe === tf
                  ? "bg-secondary/70 text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >{TF_LABELS[tf]}</button>
          ))}
        </div>
      </div>
      <div className="relative">
        <div ref={rootRef} style={{ height, width: "100%" }}/>
        {loading && <div className="absolute inset-0 flex items-center justify-center bg-background/40 text-xs text-muted-foreground">Loading bars…</div>}
        {!loading && bars?.length === 0 && <div className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground">No data.</div>}
      </div>
    </div>
  );
}
